import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  IHttpPerfSample,
  INavigationPerfSample,
  PerfChannel,
  IPerfSuggestion,
} from './models/perf-suggestion.model';
import { adviseSlowHttp, adviseSlowNavigation } from './perf-advisor.util';
import { HTTP_HISTORY_LIMIT, isSlow } from './perf-thresholds.const';

/** Keeps a bounded history of HTTP samples and turns slow ones into advice. */
@Injectable({ providedIn: 'root' })
export class PerfAdvisorService {
  private readonly history: IHttpPerfSample[] = [];
  private readonly inFlightCount$ = new BehaviorSubject(0);

  /** Number of HTTP requests currently running. */
  readonly inFlight$ = this.inFlightCount$.asObservable();

  /** Call when a request starts; `recordHttp` marks it as finished. */
  requestStarted(): void {
    this.inFlightCount$.next(this.inFlightCount$.value + 1);
  }

  /** Latest end time of any recorded request that ended at/after `sinceMs`. */
  latestHttpEndSince(sinceMs: number): number | undefined {
    return this.history.reduce<number | undefined>(
      (latest, sample) =>
        sample.endMs >= sinceMs &&
        (latest === undefined || sample.endMs > latest)
          ? sample.endMs
          : latest,
      undefined
    );
  }

  /** Records the sample; returns suggestions only if it was slow. */
  recordHttp(sample: IHttpPerfSample): IPerfSuggestion[] {
    this.history.push(sample);
    if (this.history.length > HTTP_HISTORY_LIMIT) {
      this.history.shift();
    }
    // After the push: listeners reacting to "nothing in flight" read the history.
    this.inFlightCount$.next(Math.max(0, this.inFlightCount$.value - 1));

    return isSlow(PerfChannel.HTTP, sample.endMs - sample.startMs)
      ? adviseSlowHttp(sample, this.history)
      : [];
  }

  adviseNavigation(navigation: INavigationPerfSample): IPerfSuggestion[] {
    return isSlow(PerfChannel.NAV, navigation.endMs - navigation.startMs)
      ? adviseSlowNavigation(navigation, this.history)
      : [];
  }
}
